import { useMap } from "@vis.gl/react-google-maps";
import { useCallback } from "react";

import { useSupercluster } from "@/hooks/useSupercluster";
import AppMapCluster from "components/AppMap/AppMapCluster";
import useCaseMapStore from "stores/useCaseMapStore";
import { flyTo } from "./helpers";
import { MARKER_COLOR } from "./mapLayers";
import { MachineMarker } from "./MachineMarker";

const superclusterOptions = {
  extent: 256,
  radius: 80,
  maxZoom: 14,
};

/**
 * Clustered machine pins. Unlike AppMap's marker layer, selection lives in the
 * map store rather than inside each marker, so the sidebar and the map can
 * drive each other.
 */
export function MachineMarkers({ geojson, canEditStatus }) {
  const map = useMap();
  const selectedMachineId = useCaseMapStore(s => s.selectedMachineId);
  const summaryMachineId = useCaseMapStore(s => s.summaryMachineId);
  const selectFromPin = useCaseMapStore(s => s.selectFromPin);
  const closeSummary = useCaseMapStore(s => s.closeSummary);
  const { clusters, getClusterExpansionZoom } = useSupercluster(
    geojson,
    superclusterOptions
  );

  const handleClusterClick = useCallback(
    (clusterId, position) => {
      flyTo(map, { ...position, zoom: getClusterExpansionZoom(clusterId) });
    },
    [map, getClusterExpansionZoom]
  );

  return clusters.map(feature => {
    const [lng, lat] = feature.geometry.coordinates;
    const position = { lat, lng };
    const { cluster, cluster_id, point_count, point_count_abbreviated } =
      feature.properties;

    if (cluster) {
      return (
        <AppMapCluster
          key={`cluster-${cluster_id}`}
          position={position}
          count={point_count}
          label={String(point_count_abbreviated)}
          clusterColor={MARKER_COLOR.machine}
          onClick={() => handleClusterClick(cluster_id, position)}
        />
      );
    }

    const { machine } = feature.properties;

    return (
      <MachineMarker
        key={`machine-${machine.id}`}
        machine={machine}
        position={position}
        selected={machine.id === selectedMachineId}
        summaryOpen={machine.id === summaryMachineId}
        canEditStatus={canEditStatus}
        onSelect={selectFromPin}
        onCloseSummary={closeSummary}
      />
    );
  });
}
